// src/context/loginContext.js
import { collection, getDocs, query, where } from "firebase/firestore";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from '../config/firebase';
import { createContext, useContext, useState, useEffect } from 'react';
import { registrarAcceso } from '../utils/accesosUtils';

/**
 * Verifica las credenciales del usuario en los servicios disponibles.
 */
export const checkUserCredentials = async (email, password) => {
    try {
        const emailLowerCase = email.toLowerCase().trim();
        const userCredential = await signInWithEmailAndPassword(auth, emailLowerCase, password);

        // Buscar el servicio asociado al usuario
        const serviciosRef = collection(db, "servicios");
        const q = query(serviciosRef, where("users", "array-contains", emailLowerCase));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            throw new Error('Usuario no encontrado en ningún servicio');
        }

        const servicioId = querySnapshot.docs[0].id;

        // Registro del acceso (best-effort, no bloquea el login).
        registrarAcceso({ servicioId, user: userCredential.user, origen: 'web' });

        return {
            user: userCredential.user,
            servicioId,
            error: null
        };
    } catch (error) {
        console.error("Error en checkUserCredentials:", error);
        let errorMessage = 'Error en el inicio de sesión';
        
        switch (error.code) {
            case 'auth/invalid-email':
                errorMessage = 'Correo electrónico inválido';
                break;
            case 'auth/user-disabled':
                errorMessage = 'Usuario deshabilitado';
                break;
            case 'auth/user-not-found':
                errorMessage = 'Usuario no encontrado';
                break;
            case 'auth/wrong-password':
                errorMessage = 'Contraseña incorrecta';
                break;
            case 'auth/too-many-requests':
                errorMessage = 'Demasiados intentos fallidos. Intenta más tarde';
                break;
            default:
                errorMessage = 'Error en el inicio de sesión';
        }
        
        return {
            user: null,
            servicioId: null,
            error: errorMessage
        };
    }
};

// Contexto de autenticación
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        return auth.onAuthStateChanged((user) => {
            setUser(user);
            setLoading(false);
        });
    }, []);

    const value = {
        user,
        loading,
        signIn: checkUserCredentials,
        signOut: () => auth.signOut()
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};